/**
 * Fills the inline ShopperApproved review count on the promo/discount landing
 * pages (savings, savings1, doctors-discount, amri-back-to-school).
 *
 * The page body carries `<span class="review-count js-sa-count">…</span>` inside
 * the "⭐⭐⭐⭐⭐ 4.9 (… reviews)" rating line — a save-safe, block-free 1:1 stand-in
 * for the legacy inline `ReviewsCountWidget`. This module fetches the cached
 * count from the server (`GET /api/reviews/count`, token stays server-side) and
 * writes it in, formatted with thousands separators. Progressive enhancement:
 * the span ships with the legacy default (15,125) baked in, so if the fetch
 * fails or JS is disabled the line still reads sensibly.
 *
 * Self-guarding: no-ops when the page has no `.js-sa-count`, so it is safe to
 * load on any `page`-doctype template.
 */

async function fillReviewCount(): Promise<void> {
    const targets = document.querySelectorAll<HTMLElement>('.js-sa-count');
    if (targets.length === 0) {
        return;
    }

    try {
        const resp = await fetch('/api/reviews/count', {
            headers: { Accept: 'application/json' },
        });
        if (!resp.ok) {
            return;
        }

        const data = (await resp.json()) as { count?: number };
        if (typeof data.count !== 'number' || data.count <= 0) {
            return;
        }

        const formatted = data.count.toLocaleString('en-US');
        targets.forEach((el) => {
            el.textContent = formatted;
        });
    } catch {
        // Leave the baked-in fallback in place on any network/parse error.
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => void fillReviewCount());
} else {
    void fillReviewCount();
}
